/**
 * The action range (Phase 59.4): a measuring stick for real-time combat feel.
 *
 * One action bot (its own sight, aim and trigger; held in place so the distance stays what
 * was asked) against one scripted target at a set distance — standing, strafing, or strafing
 * and hopping. Reports hit rate, time to first shot and time to kill, so walking speed, jumps,
 * fire rate, spread, bullet speed and bot skill are tuned from numbers. Deterministic: the
 * same setup gives the same result. Pure host over Match; no renderer.
 */

import type { GameState, PlayerState, ScaleProfileName, UnitIntent, Vec3, GroundWeaponKind } from '../types';
import type { ActionEvent, SimPresenter } from '../match/match';
import { IDLE_INTENT } from '../match/match';
import { createMatchState, matchFromState } from '../match/setup';
import { createActionBot } from './actionBot';
import { duelMatchSetup } from './actionDuel';
import { headingOf } from '../core/surfaceWalk';
import { hasLineOfSight, losEyePoint } from '../core/los';
import { segmentCovered } from '../core/bullets';
import { vecAdd, vecScale, vecNormalize, vecSub, vecLength } from '../math';
import { dsin, dcos } from '../core/dmath';
import { PHYS_DT } from '../constants';
import { SeededRandom } from '../core/random';
import { personEyePoint, personLineOfSight } from '../core/fineTerrain';
import { personSetOf } from '../core/scaleProfile';
import { CONFIG_FLIPS_OFF } from '../core/defaultOn';

export type RangeMotion = 'still' | 'strafe' | 'hop';

export interface RangeSetup {
  seed: string;
  /** Shooter's bot skill, 0–1. */
  skill: number;
  /** Arc distance to the target (units). */
  distance: number;
  motion: RangeMotion;
  /** Seconds before the trial gives up (default 10). */
  timeLimit?: number;
  /** The scale profile (WORKBOARD S1); distances are then in person-lengths × k. */
  scale?: ScaleProfileName;
  /** 65.6: the weapon the shooter holds throughout (no switching; default the rifle, today's gun). */
  weapon?: GroundWeaponKind;
  /** 62.52: wind on the action clock — decision 120's switch on (absent = calm, today's range); with no `weather`, under a fixed storm (the hardest wind). */
  wind?: boolean;
  /** 62.52: a fixed weather for the whole trial (absent = none, today's range) — so a storm's wind is measured against the same storm calm. */
  weather?: import('../core/weather').WeatherKind;
  /** 65.33: × every bullet's muzzle speed (the Lab's Bullet speed; absent = the scale's: human 2). */
  bulletSpeed?: number;
  /** 185: hit locations on (the head ×2.5 …), for the bots' head aim by distance. */
  hitLocations?: boolean;
}

export interface RangeResult {
  shots: number;
  hits: number;
  killed: boolean;
  /** How the shooter's misses ended. */
  misses: Record<string, number>;
  /** Seconds from the start to the first shot, or null. */
  firstShot: number | null;
  /** Seconds from the start to the kill, or null. */
  timeToKill: number | null;
  /** False when no clear line could be found at this distance on this seed (the trial is void). */
  valid: boolean;
  /** 65.27: the shooter's hits by where they landed (with hit locations on; DECISIONS 300's chest aim measured by it). */
  locations: Record<string, number>;
}

/** The target's script: strafe across the line of fire, reversing on a rhythm; hop if asked. */
function targetIntent(motion: RangeMotion, tick: number): UnitIntent {
  if (motion === 'still') return IDLE_INTENT;
  const t = tick * PHYS_DT;
  const period = 1.1;
  const right = Math.floor(t / period) % 2 === 0 ? 1 : -1;
  const jump = motion === 'hop' && tick % Math.round(0.9 / PHYS_DT) === 30;
  return { ...IDLE_INTENT, right, jump };
}

/** Point `dir` moved `arc` units along `heading` over a sphere of `radius`. */
function along(dir: Vec3, heading: Vec3, arc: number, radius: number): Vec3 {
  const th = arc / radius;
  return vecNormalize(vecAdd(vecScale(dir, dcos(th)), vecScale(heading, dsin(th))));
}

export function runRange(setup: RangeSetup): RangeResult {
  const { state } = createMatchState(duelMatchSetup({ seed: setup.seed, teams: 2, unitsPerTeam: 1, difficulty: ['hard', 'hard'], ...(setup.scale ? { scale: setup.scale } : {}), ...(setup.wind ? { wind: true } : { wind: false }), ...(setup.weather || setup.wind ? { weatherFixed: setup.weather ?? 'storm' } : { weather: false }), planetPick: 'stars', ...(setup.bulletSpeed !== undefined ? { personTuning: { bulletSpeed: setup.bulletSpeed } } : {}), ...CONFIG_FLIPS_OFF, hitLocations: setup.hitLocations === true }));   // calm unless asked (decision 147 turned them on for matches); its old world (67.41's follow-up; DECISIONS 300's flips off, hit locations only when asked)
  let s: GameState = { ...state, phase: 'aiming' };
  const shooter0 = s.players.find(p => p.teamIndex === 0)!;
  const target0 = s.players.find(p => p.teamIndex === 1)!;
  const planet = s.planets[shooter0.planetIndex];

  // Put the target `distance` ahead of the shooter, turning (and, failing that, moving the
  // shooter to other spots on the planet) until the line between their eyes is clear
  let placed: { shooter: PlayerState; target: PlayerState } | null = null;
  const rng = new SeededRandom(`${setup.seed}-range-spot`);
  for (let spot = 0; spot < 12 && !placed; spot++) {
    let n = vecNormalize(shooter0.surfaceDir);
    if (spot > 0) {
      const a = rng.next() * 2 * Math.PI, z = rng.next() * 2 - 1, r = Math.sqrt(1 - z * z);
      n = { x: r * dcos(a), y: r * dsin(a), z };
    }
    const h0 = headingOf({ surfaceDir: n });
    const right = vecNormalize({ x: n.y * h0.z - n.z * h0.y, y: n.z * h0.x - n.x * h0.z, z: n.x * h0.y - n.y * h0.x });
    for (let k = 0; k < 24 && !placed; k++) {
      const a = (k / 24) * 2 * Math.PI;
      const heading = vecNormalize(vecAdd(vecScale(h0, dcos(a)), vecScale(right, dsin(a))));
      const tdir = along(n, heading, setup.distance, planet.radius);
      const shooter: PlayerState = { ...shooter0, surfaceDir: n, heading, botConfig: { ...shooter0.botConfig!, skill: setup.skill }, ...(setup.weapon && setup.weapon !== 'rifle' ? (setup.weapon === 'smg' || setup.weapon === 'lmg' ? { primary: setup.weapon } : { weapon: setup.weapon }) : {}) };   // 65.13 / 65.14: a long gun is the shooter's primary
      const target: PlayerState = { ...target0, planetIndex: shooter0.planetIndex, surfaceDir: tdir, isBot: true };
      const players = s.players.map(p => p.id === shooter.id ? shooter : p.id === target.id ? target : p);
      const test = { ...s, players };
      const eyeA = personEyePoint(shooter, s.planets, personSetOf(s), personSetOf(s).losEyeHeight), eyeB = personEyePoint(target, s.planets, personSetOf(s), personSetOf(s).losEyeHeight);
      if (personLineOfSight(eyeA, eyeB, s) && !segmentCovered(eyeA, eyeB, test)) { placed = { shooter, target }; s = test; }
    }
  }
  if (!placed) return { shots: 0, hits: 0, misses: {}, killed: false, firstShot: null, timeToKill: null, valid: false, locations: {} };

  const match = matchFromState(s);
  const bot = createActionBot(setup.seed);
  const shooterId = placed.shooter.id, targetId = placed.target.id;
  match.actionDriver = (m, unit) => {
    if (unit.id === targetId) return targetIntent(setup.motion, m.actionTick);
    const i = bot.drive(m, unit);
    return unit.id === shooterId ? { ...i, forward: 0, right: 0, jump: false, weapon: undefined } : i;   // held: no walking, no switching
  };

  const misses: Record<string, number> = {};
  const locations: Record<string, number> = {};
  let shots = 0, hits = 0, firstShot: number | null = null, timeToKill: number | null = null;
  const onEvents = (events: ActionEvent[]) => {
    const t = match.actionTick * PHYS_DT;
    for (const e of events) {
      if (e.kind === 'fired' && e.unitId === shooterId) { shots++; if (firstShot === null) firstShot = t; }
      if (e.kind === 'hitUnit' && e.bullet.ownerId === shooterId) { hits++; if (e.location) locations[e.location] = (locations[e.location] ?? 0) + 1; if (e.killed) timeToKill = t; }
      else if ((e.kind === 'hitGround' || e.kind === 'hitCover' || e.kind === 'expired') && e.bullet.ownerId === shooterId) misses[e.kind] = (misses[e.kind] ?? 0) + 1;
    }
  };
  const noop = () => {};
  const p: SimPresenter = {
    holdFlightEnd: () => false, flightEndReached: noop, launched: noop, probeScan: noop,
    geyserDeflect: noop, impacted: noop, lost: noop, flightOver: noop, airborne: noop,
    actionEvents: e => onEvents(e),
  };
  const limit = Math.round((setup.timeLimit ?? 10) / PHYS_DT);
  match.runTicks(limit - match.actionTick, p, () => timeToKill !== null);
  return { shots, hits, misses, killed: timeToKill !== null, firstShot, timeToKill, valid: true, locations };
}

/** Phase 59.4 browser range: where the practice targets stand and how each moves. */
export const RANGE_LAYOUT: { distance: number; motion: RangeMotion }[] = [
  { distance: 6, motion: 'still' }, { distance: 9, motion: 'still' }, { distance: 12, motion: 'still' },
  { distance: 6, motion: 'strafe' }, { distance: 9, motion: 'strafe' }, { distance: 12, motion: 'hop' },
];

/**
 * Lay out the practice range in a match whose team 1 has RANGE_LAYOUT.length units: each
 * target at its distance from the shooter (team 0's unit), spread round the compass, each in
 * clear sight of the shooter's eye. Pure; returns the arranged state and each target's motion.
 */
export function arrangeRange(state: GameState, seed: string): { state: GameState; motions: Map<number, RangeMotion> } {
  const shooter = state.players.find(p => p.teamIndex === 0)!;
  const targets = state.players.filter(p => p.teamIndex === 1);
  const planet = state.planets[shooter.planetIndex];
  const n = vecNormalize(shooter.surfaceDir);
  // (the frame's own bearing, not the shooter's start facing: 69.6d turns a spawn toward the enemy's centre — the targets
  // where the duel put them, before this moves them — which turned the whole layout, its lines of sight landing on other
  // ground: the smoke's range took 97.8 simulated s, not 38.2. The range has no objective; slot 0 straight ahead)
  const h0 = headingOf({ surfaceDir: n });
  const right = vecNormalize({ x: n.y * h0.z - n.z * h0.y, y: n.z * h0.x - n.x * h0.z, z: n.x * h0.y - n.y * h0.x });
  const eye = personEyePoint(shooter, state.planets, personSetOf(state), personSetOf(state).losEyeHeight);
  const motions = new Map<number, RangeMotion>();
  let players = state.players;
  targets.forEach((t, i) => {
    const slot = RANGE_LAYOUT[i % RANGE_LAYOUT.length];
    const base = (i / targets.length) * 2 * Math.PI;
    // All the way round from its own bearing; then a unit closer at a time (small planets hide 12 u)
    let chosen: Vec3 | null = null;
    for (let d = slot.distance; d >= 4 && !chosen; d -= 1) {
      for (let k = 0; k < 24 && !chosen; k++) {
        const a = base + (k % 2 === 0 ? 1 : -1) * Math.ceil(k / 2) * (Math.PI / 12);
        const heading = vecNormalize(vecAdd(vecScale(h0, dcos(a)), vecScale(right, dsin(a))));
        const dir = along(n, heading, d, planet.radius);
        const tooClose = players.some(p => p.teamIndex === 1 && p.id !== t.id && motions.has(p.id) && vecLength(vecSub(p.surfaceDir, dir)) * planet.radius < 2);
        const probeEye = personEyePoint({ ...t, planetIndex: shooter.planetIndex, surfaceDir: dir }, state.planets, personSetOf(state), personSetOf(state).losEyeHeight);
        if (!tooClose && personLineOfSight(eye, probeEye, state) && !segmentCovered(eye, probeEye, state)) chosen = dir;
      }
    }
    const dir = chosen ?? along(n, vecNormalize(vecAdd(vecScale(h0, dcos(base)), vecScale(right, dsin(base)))), slot.distance, planet.radius);
    players = players.map(p => p.id === t.id ? { ...p, planetIndex: shooter.planetIndex, surfaceDir: dir, isBot: true, holdFire: true } : p);
    motions.set(t.id, slot.motion);
  });
  // the shooter faces the frame's bearing again, slot 0 straight ahead, as the range was laid out
  players = players.map(p => (p.id === shooter.id ? { ...p, heading: undefined } : p));
  return { state: { ...state, players }, motions };
}

/** The range's driver: scripted targets; anyone else (the shooter, if a bot) plays as an action bot. */
export function rangeDriver(seed: string, motions: Map<number, RangeMotion>): (m: import('../match/decide').DecideView, unit: PlayerState) => UnitIntent {
  const bot = createActionBot(seed);
  return (m, unit) => {
    const motion = motions.get(unit.id);
    return motion ? targetIntent(motion, m.actionTick + unit.id * 37) : bot.drive(m, unit);
  };
}
